// File: src/components/AdminLoginWrapper.jsx
import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';

/** 
 * AdminLoginWrapper:
 * 1) If user is not logged in (no adminToken in localStorage), show the 2-step login flow.
 * 2) On successful verification, store the token & show the wrapped children, with a logout button.
 * 3) We create a socket if/when we have an admin token (on mount or after verify).
 */

export default function AdminLoginWrapper({ children }) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [step, setStep] = useState('login'); // 'login' | 'verify'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Hold the socket instance in state
  const [socket, setSocket] = useState(null);

  /**
   * 1) On mount => if there's already an adminToken, set isLoggedIn = true 
   *    AND create the socket. This allows refresh persistence without "null token."
   */
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      setIsLoggedIn(true);

      // Create the socket for an existing token
      const newSocket = io(`${process.env.REACT_APP_API_URL}`, {
        auth: { token },
      });
      setSocket(newSocket);
    }
  }, []);

  // Step 1: email + password => POST /admin/auth/login
  async function handleLogin(e) {
    e.preventDefault();
    setErrorMsg('');
    setLoading(true);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || 'Login failed');
      }
      // If success => proceed to code verification step
      setStep('verify');
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setLoading(false);
    }
  }

  // Step 2: code => POST /admin/auth/verify => returns token
  async function handleVerify(e) {
    e.preventDefault();
    setErrorMsg('');
    setLoading(true);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/auth/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || 'Verification failed');
      }
      const data = await res.json();

      // data.token => store in localStorage
      if (!data.token) {
        throw new Error('No token returned from server');
      }
      localStorage.setItem('adminToken', data.token);

      // Mark as logged in
      setIsLoggedIn(true);

      // Create the socket after we have a fresh token
      const newSocket = io(`${process.env.REACT_APP_API_URL}`, {
        auth: { token: data.token },
      });
      setSocket(newSocket);

    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setLoading(false);
    }
  }

  // Logout
  function handleLogout() {
    localStorage.removeItem('adminToken');
    setIsLoggedIn(false);
    setStep('login'); // optional: return to login step
    setEmail('');
    setPassword('');
    setCode('');

    // If socket is active, disconnect
    if (socket) {
      socket.disconnect();
      setSocket(null);
    }
  }

  // If already logged in, just render children with a logout button
  if (isLoggedIn) {
    return (
      <div style={{ maxWidth: 10000, margin: '0rem 0rem', fontFamily: 'sans-serif' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
          <button onClick={handleLogout} style={{ padding: '0.5rem 1rem' }}>
            Log Out
          </button>
        </div>
        {children}
      </div>
    );
  }

  // Otherwise, show the login flow
  return (
    <div style={{ maxWidth: '400px', margin: '2rem auto', fontFamily: 'sans-serif' }}>
      <h2 style={{ marginBottom: '1rem' }}>Admin Login Required</h2>

      {step === 'login' && (
        <div style={{ border: '1px solid #ccc', padding: '1rem', borderRadius: '6px' }}>
          <h3 style={{ marginTop: 0 }}>Step 1: Email + Password</h3>
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '0.75rem' }}>
              <label>Email:</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
                required
              />
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <label>Password:</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
                required
              />
            </div>
            {errorMsg && <p style={{ color: 'red' }}>{errorMsg}</p>}
            <button type="submit" style={{ padding: '0.5rem 1rem' }} disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      )}

      {step === 'verify' && (
        <div style={{ border: '1px solid #ccc', padding: '1rem', borderRadius: '6px' }}>
          <h3 style={{ marginTop: 0 }}>Step 2: Verify Code</h3>
          <p>We emailed a code to {email}. Enter it below:</p>
          <form onSubmit={handleVerify}>
            <div style={{ marginBottom: '0.75rem' }}>
              <label>Code:</label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
                required
              />
            </div>
            {errorMsg && <p style={{ color: 'red' }}>{errorMsg}</p>}
            <button type="submit" style={{ padding: '0.5rem 1rem' }} disabled={loading}>
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
